import { useContext, useEffect, useState } from "react";
import {
  haveValue,
  convertCurrencyFormat,
  getUserDisplayName,
  toast,
  shouldDeductNextView,
} from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  calculateNowTime,
  getProfileReminders,
} from "../../services/sharedService";
import moment from "moment";
import AuthContext from "../../context/auth/AuthContext";
import LandLordContext from "../../context/landlord/LandLordContext";
import AddPropertyModal from "../../pages/landlord/profile-request/AddPropertyModal";
import ServingPromptModal from "../../pages/landlord/profile-request/ServingPromptModal";
import { WORKSPACE_ROLES_CAPABILITIES } from "../../Utils/workspaceRoleCapabilities";
import DocsStatus from "./DocsStatus";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";

const AccordionRow = ({
  profile,
  aggregatedData,
  isOpen,
  onToggle,
  properties,
  loadData,
  totalParticipants,
  tenantChecks = false,
}) => {
  const { _t } = useTranslator();
  const authContext = useContext(AuthContext);
  const { user, setLoading } = authContext;
  const landLordContext = useContext(LandLordContext);
  const { propertiesAll, editSharedProfile } = landLordContext;
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const [linkedPropertiesModel, setLinkedPropertiesModel] = useState(false);
  const [propertyLink, setPropertyLink] = useState([]);
  const [servingPromptModal, setServingPromptModal] = useState(false);
  const [reminderSentAt, setReminderSentAt] = useState(null);
  const availablePropertyIds = new Set(
    (propertiesAll ?? []).map((property) => property?._id?.toString())
  );
  const linkedPropertyCount = (propertyLink ?? []).filter((propertyId) =>
    availablePropertyIds.has(propertyId?.toString())
  ).length;
  const isHighlight =
    profile?.opened_at &&
    profile?.sortDate &&
    new Date(profile.sortDate) > new Date(profile.opened_at);

  const participants = profile?.application_id?.participants;
  const allParticipantsOutdated =
    participants?.length > 0 &&
    participants.every((p) => p.tenant_info?.is_outdated);

  const missingCount =
    profile?.application_id?.participants?.reduce((total, p) => {
      return total + (p.tenant_info?.missingCount || 0);
    }, 0) || 0;

  useEffect(() => {
    const tenantId = profile?.tenantid?._id;
    if (!allParticipantsOutdated || !tenantId) {
      setReminderSentAt(null);
      return;
    }
    let cancelled = false;
    getProfileReminders(tenantId)
      .then((response) => {
        if (!cancelled) {
          setReminderSentAt(response?.data?.[0]?.sent_at || null);
        }
      })
      .catch(() => {
        if (!cancelled) setReminderSentAt(null);
      });
    return () => {
      cancelled = true;
    };
  }, [allParticipantsOutdated, profile?.tenantid?._id]);

  const applicationMembers =
    profile?.application_id?.participants
      ?.filter((p) => p.consent_status === "accepted" && p.tenant_info)
      ?.map((p) => getUserDisplayName(p.tenant_info))
      ?.filter(Boolean) ?? [];

  const applicationName = profile?.application_id?.title;
  const subtitleMembers = applicationMembers.slice(1, 3).join(", ");
  const hasMoreMembers = applicationMembers.length > 3;

  useEffect(() => {
    if (haveValue(properties) && haveValue(profile?.propertyid)) {
      setPropertyLink(profile?.propertyid);
    }
  }, [properties, profile]);

  const refreshProfilesInBackground = () => {
    Promise.resolve(loadData()).catch((err) => {
      console.log("Error refreshing profiles after property update", err);
    });
  };

  const handleLinkProperty = async (
    propertyToAdd = null,
    propertyToRemove = null
  ) => {
    if (haveValue(propertyToAdd)) {
      const data = {
        property_id: propertyToAdd,
        status: "add",
      };

      const response = await editSharedProfile(profile?._id, data);
      if (response) {
        setPropertyLink((currentIds) =>
          currentIds.some((id) => id?.toString() === propertyToAdd?.toString())
            ? currentIds
            : [...currentIds, propertyToAdd]
        );
        refreshProfilesInBackground();
      }
      return Boolean(response);
    } else if (haveValue(propertyToRemove)) {
      const data = {
        property_id: propertyToRemove,
        status: "remove",
      };

      const response = await editSharedProfile(profile?._id, data);
      if (response) {
        setPropertyLink((currentIds) =>
          currentIds.filter(
            (id) => id?.toString() !== propertyToRemove?.toString()
          )
        );
        refreshProfilesInBackground();
      }
      return Boolean(response);
    } else {
      toast.error(_t("pleaseSelectPropertyToLink"));
    }
  };

  const handleUnlinkProperty = async (propertyId) => {
    await handleLinkProperty(null, propertyId);
  };

  const handleOpenProfile = async () => {
    try {
      setLoading(true);

      const checkView = await shouldDeductNextView(user, profile?._id);

      if (checkView) {
        setServingPromptModal(true);
      } else {
        navigate(
          `/${workspaceId}/profile-property?sharedprofileid=${profile?._id}`,
          {
            state: { from: location.pathname + location.search },
          }
        );
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };
  const handleSkorProtect = () => {
    window.open(
      "https://www.skortorent.com/inquilino/skor-protect",
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      <tr
        className={`hideOnMobile second-row relative accordion-row ${
          isHighlight ? "highlightRow" : ""
        } ${isOpen ? "accordion-open" : ""} ${allParticipantsOutdated ? "dimmedRow" : ""}`}
        onClick={onToggle}
      >
        <td>
          <div
            className="d-flex align-items-center custom-gap-3 profile-accordion-tenant-cell"
            style={{ marginInlineEnd: "0.5vw" }}
          >
            <div className="position-relative list-member-wrapper">
              <div
                className="earner-thumbnail-wrapper d-flex align-items-center justify-content-center"
                style={{
                  position: "relative",
                  padding: "0.2vw",
                }}
              >
                <img
                  src="/images/multiple_users_icon_unfilled.svg"
                  alt="User Icon"
                  style={{ objectFit: "contain" }}
                />
              </div>

              {totalParticipants && (
                <div className="member-count-wrapper d-flex align-items-center justify-content-center">
                  {totalParticipants}
                </div>
              )}
            </div>

            <div className="d-flex flex-column">
              <div className="d-flex align-items-center custom-gap-2">
                <span className="application-title">{applicationName}</span>
              </div>

              {subtitleMembers ? (
                <span className="application-subtitle">
                  {_t("with", "with")} {subtitleMembers}
                  {hasMoreMembers ? "..." : ""}
                </span>
              ) : null}
            </div>

            {totalParticipants > 0 && (
              <img
                src="/images/arrow-right.svg"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                alt="Arrow Down"
                className="cursor-pointer"
                style={{
                  transform: !isOpen ? "rotate(90deg)" : "rotate(270deg)",
                }}
              />
            )}

            {profile?.application_id?.skor_protect && (
              <>
                <span
                  data-tooltip-id={`isf-sp-badge-tooltip-${profile?._id}`}
                  className="d-flex align-items-center"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <SkorProtectBadge />
                </span>
                <ReactTooltip
                  id={`isf-sp-badge-tooltip-${profile?._id}`}
                  place="bottom"
                  className="react-tooltip-styling react-tooltip-styling-skor-protect"
                  clickable
                  style={{
                    maxWidth: "330px",
                    whiteSpace: "normal",
                  }}
                  render={() => (
                    <p style={{ lineHeight: 1.2 }}>
                      {_t("skorProtectTooltipDescription")}{" "}
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSkorProtect();
                        }}
                        style={{
                          cursor: "pointer",
                          textDecoration: "underline",
                          fontWeight: "400",
                        }}
                      >
                        {_t("skorProtectTooltipLink")}
                      </span>
                    </p>
                  )}
                />
              </>
            )}
          </div>
        </td>

        <td className="skor-details-column">{aggregatedData.totalScore}</td>

        <td>€{convertCurrencyFormat(aggregatedData.totalIncome)}</td>

        <td>€{convertCurrencyFormat(aggregatedData.totalEffortRate)}</td>

        <td>
          <div className="d-flex align-items-center">
            <div className="d-flex align-items-center bg-[#DBF1CA] bg_test removeGreenBg">
              <p className="linked_p">{aggregatedData.totalGuarantors}</p>
            </div>
          </div>
        </td>

        <td>
          <div className="d-flex align-items-center">
            <div
              className={`d-flex align-items-center bg-[#DBF1CA] bg_test removeGreenBg cursor-pointer ${
                linkedPropertyCount === 0 ? "btngreen" : "link_property_bg"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setLinkedPropertiesModel(true);
              }}
            >
              <div className="d-flex align-items-center custom-gap-2">
                <p className="linked_p">{linkedPropertyCount}</p>

                <img
                  src="/images/arrow-right-green.svg"
                  alt="Open properties"
                />

                {WORKSPACE_ROLES_CAPABILITIES?.properties?.linkTenant?.includes(
                  user?.current_workspace_role
                ) && (
                  <div className="cursor-pointer editButton">
                    <img src="/images/addGreen.svg" alt="Add property" />
                    <p>{_t("add")}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </td>

        {!tenantChecks && (
          <td>
            <DocsStatus missingCount={missingCount} />
          </td>
        )}

        <td>
          <div className="d-flex custom-gap-2 align-items-center">
            {allParticipantsOutdated ? (
              <div
                className="d-flex flex-column"
                style={{ alignItems: "flex-start", gap: "2px" }}
              >
                <span className="outdatedBadge">{_t("outdated")}</span>
                {reminderSentAt && (
                  <span
                    className="profileReminderDate"
                    style={{ fontSize: "0.7rem" }}
                  >
                    {_t("reminderSent", "Reminder sent")}{" "}
                    {moment(reminderSentAt)
                      .locale(
                        (localStorage.getItem("i18nextLng") || "").startsWith(
                          "es"
                        )
                          ? "es"
                          : "en-gb"
                      )
                      .fromNow()}
                  </span>
                )}
              </div>
            ) : (
              <div className="d-flex align-items-center custom-gap-2">
                {calculateNowTime(
                  aggregatedData.latestUpdateDate,
                  localStorage.getItem("i18nextLng")
                )}
              </div>
            )}

            {!allParticipantsOutdated && isHighlight && (
              <p className="recentText">{_t("recent")}</p>
            )}
          </div>
        </td>

        <td
          onClick={(e) => {
            e.stopPropagation();
            handleOpenProfile();
          }}
        >
          <div className="cursor-pointer">
            <img
              className="arrowRightTable"
              src="/images/arrow-right-green.svg"
              alt="Open profile"
            />
          </div>
        </td>
      </tr>

      <div className="hideOnDesktop">
        <div
          className={`accordion-tile ${
            isHighlight ? "highlightRow" : ""
          } ${isOpen ? "accordion-open" : ""} ${allParticipantsOutdated ? "dimmedMobileCard" : ""}`}
          onClick={onToggle}
        >
          <div className="mobile-profile-info-box d-flex align-items-center mobile-profile-gap">
            <div className="position-relative">
              <div
                className="earner-thumbnail-wrapper d-flex align-items-center justify-content-center accordion-trigger-avatar"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                style={{
                  position: "relative",
                  padding: "8px",
                }}
              >
                <img
                  src="/images/multiple_users_icon_unfilled.svg"
                  alt="User Icon"
                  style={{
                    objectFit: "contain",
                    width: "32px",
                    height: "32px",
                  }}
                />
                {totalParticipants && (
                  <div className="member-count-wrapper d-flex align-items-center justify-content-center">
                    {totalParticipants}
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex flex-column mobile-profile-info-box">
              <div className="d-flex align-items-center custom-gap-2">
                <span
                  className="application-title"
                  style={{ lineHeight: "normal" }}
                >
                  {applicationName || aggregatedData.tenantName}
                </span>
              </div>

              {subtitleMembers ? (
                <span className="application-subtitle">
                  {_t("with", "with")} {subtitleMembers}
                  {hasMoreMembers ? "..." : ""}
                </span>
              ) : null}
              {profile?.application_id?.skor_protect && (
                <>
                  <span
                    data-tooltip-id={`isf-sp-badge-tooltip-${profile?._id}`}
                    className="d-flex align-items-center mt-1"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <SkorProtectBadge />
                  </span>
                  <ReactTooltip
                    id={`isf-sp-badge-tooltip-${profile?._id}`}
                    place="bottom"
                    className="react-tooltip-styling react-tooltip-styling-skor-protect"
                    clickable
                    style={{
                      maxWidth: "330px",
                      whiteSpace: "normal",
                    }}
                    render={() => (
                      <p
                        style={{
                          lineHeight: 1.2,
                        }}
                      >
                        {_t("skorProtectTooltipDescription")}{" "}
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSkorProtect();
                          }}
                          style={{
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          {_t("skorProtectTooltipLink")}
                        </span>
                      </p>
                    )}
                  />
                </>
              )}
            </div>
          </div>

          <div className="mobile-profile-details table-mobile-card-comp">
            {/* <div className="mobile-profile-detail-item">
              <span className="detail-label">{_t("skor")}</span>
              <span className="detail-value">{aggregatedData.totalScore}</span>
            </div> */}

            <div className="mobile-profile-detail-item">
              <span className="detail-label">{_t("income")}</span>
              <span className="detail-value">
                €{convertCurrencyFormat(aggregatedData.totalIncome)}
              </span>
            </div>

            <div className="mobile-profile-detail-item">
              <span className="detail-label">{_t("maxRent")}</span>
              <span className="detail-value">
                €{convertCurrencyFormat(aggregatedData.totalEffortRate)}
              </span>
            </div>

            {/* <div className="mobile-profile-detail-item">
              <span className="detail-label">{_t("members")}</span>
              <span className="detail-value">
                {profile?.application_id?.participants?.length ??
                  aggregatedData.totalFlatmates}
              </span>
            </div> */}

            <div className="mobile-profile-detail-item">
              <span className="detail-label">{_t("guarantors")}</span>
              <span className="detail-value">
                {aggregatedData.totalGuarantors}
              </span>
            </div>

            <div className="mobile-profile-detail-item">
              <span className="detail-label">{_t("linkedProperties")}</span>
              <span className="detail-value">
                <div
                  className={`d-flex align-items-center bg-[#DBF1CA] bg_test removeGreenBg ${
                    linkedPropertyCount === 0 ? "btngreen" : "link_property_bg"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLinkedPropertiesModel(true);
                  }}
                >
                  <div className="d-flex align-items-center custom-gap-2">
                    <p className="linked_p">{linkedPropertyCount}</p>

                    <img
                      src="/images/arrow-right-green.svg"
                      alt="Open properties"
                      style={{ width: "14px", height: "14px" }}
                    />
                  </div>
                </div>
              </span>
            </div>

            {!tenantChecks && (
              <div className="mobile-profile-detail-item">
                <span className="detail-label">{_t("docs")}</span>
                <span className="detail-value">
                  <DocsStatus missingCount={missingCount} />
                </span>
              </div>
            )}
          </div>

          <div className="mobile-profile-footer">
            <div className="d-flex custom-gap-2 align-items-center">
              {allParticipantsOutdated ? (
                <div
                  className="d-flex flex-column"
                  style={{ alignItems: "flex-start", gap: "2px" }}
                >
                  <span className="outdatedBadge">{_t("outdated")}</span>
                  {reminderSentAt && (
                    <span
                      className="profileReminderDate"
                      style={{ fontSize: "0.7rem" }}
                    >
                      {_t("reminderSent", "Reminder sent")}{" "}
                      {moment(reminderSentAt)
                        .locale(
                          (localStorage.getItem("i18nextLng") || "").startsWith(
                            "es"
                          )
                            ? "es"
                            : "en-gb"
                        )
                        .fromNow()}
                    </span>
                  )}
                </div>
              ) : (
                <div
                  className="d-flex align-items-center custom-gap-2"
                  style={{ fontSize: "11px" }}
                >
                  {calculateNowTime(
                    aggregatedData.latestUpdateDate,
                    localStorage.getItem("i18nextLng")
                  )}
                </div>
              )}

              {!allParticipantsOutdated && isHighlight && (
                <p className="recentText">{_t("recent")}</p>
              )}
            </div>

            <div
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenProfile();
              }}
            >
              <img
                className="arrowRightTable"
                src="/images/arrow-right-green.svg"
                alt="Open profile"
                style={{ width: "16px", height: "16px" }}
              />
            </div>
          </div>
        </div>
      </div>

      <AddPropertyModal
        isOpen={linkedPropertiesModel}
        onClose={() => setLinkedPropertiesModel(false)}
        properties={properties}
        profile={profile}
        linkedPropertyIds={propertyLink}
        handleUnlinkProperty={(id) => handleUnlinkProperty(id)}
        handleLinkProperty={handleLinkProperty}
      />

      <ServingPromptModal
        openModal={servingPromptModal}
        profile={profile}
        URL={`/${workspaceId}/profile-property?sharedprofileid=${profile?._id}`}
        setOpenModal={setServingPromptModal}
      />
    </>
  );
};

export default AccordionRow;
